interface RateLimitInfo {
  nextAllowedTime: number;
  attempts: number;
  lastAttempt: number;
  lastSuccess: number;
}

interface RateLimits {
  [key: string]: RateLimitInfo | undefined;
}

const RATE_LIMITS_KEY = 'metaApiRateLimits';

function getRateLimits(): RateLimits {
  try {
    const storedLimits = localStorage.getItem(RATE_LIMITS_KEY);
    return storedLimits ? JSON.parse(storedLimits) : {};
  } catch (error) {
    console.error("Error getting rate limits from localStorage:", error);
    return {};
  }
}

function saveRateLimits(limits: RateLimits): void {
  try {
    localStorage.setItem(RATE_LIMITS_KEY, JSON.stringify(limits));
  } catch (error) {
    console.error("Error saving rate limits to localStorage:", error);
  }
}

export async function enforceRequestInterval(key: string, intervalSeconds: number = 60): Promise<void> {
  const now = Date.now();
  const rateLimits = getRateLimits();
  const limitInfo = rateLimits[key] || { nextAllowedTime: 0, attempts: 0, lastAttempt: 0, lastSuccess: 0 };
  
  if (limitInfo.nextAllowedTime > now) {
    const delay = limitInfo.nextAllowedTime - now;
    console.warn(`Rate limit hit for ${key}, waiting ${delay}ms`);
    
    // Shorter delay to avoid blocking the UI thread
    await new Promise(resolve => setTimeout(resolve, delay));
  }
}

export function trackSuccessfulRequest(key: string): void {
  const now = Date.now();
  const rateLimits = getRateLimits();
  
  rateLimits[key] = {
    ...(rateLimits[key] || { attempts: 0, nextAllowedTime: 0 }),
    lastAttempt: now,
    lastSuccess: now,
    nextAllowedTime: 0 // Reset cooldown on success
  };
  
  saveRateLimits(rateLimits);
}

export function handleRateLimitError(error: any): boolean {
  if (error?.message?.includes('Ratenbegrenzung erreicht')) {
    const rateLimits = getRateLimits();
    
    // Set a global rate limit for 15 minutes
    const now = Date.now();
    const nextAllowedTime = now + (15 * 60 * 1000);
    
    // Set rate limits for all known endpoints
    Object.keys(rateLimits).forEach(key => {
      rateLimits[key] = {
        ...rateLimits[key],
        nextAllowedTime,
        attempts: (rateLimits[key]?.attempts || 0) + 1,
        lastAttempt: now
      };
    });
    
    saveRateLimits(rateLimits);
    
    console.warn('Global rate limit activated due to error.');
    return true;
  }
  
  return false;
}

export function canForceRefresh(key: string): boolean {
  const rateLimits = getRateLimits();
  const limitInfo = rateLimits[key];
  
  if (!limitInfo || !limitInfo.lastSuccess) {
    return true; // Allow if never succeeded
  }
  
  const now = Date.now();
  const timeSinceLastSuccess = now - limitInfo.lastSuccess;
  
  // Allow force refresh if last success was more than 30 minutes ago
  return timeSinceLastSuccess > (30 * 60 * 1000);
}

export function reduceRateLimitCooldown(key: string, reductionFactor: number): void {
  const rateLimits = getRateLimits();
  const limitInfo = rateLimits[key];
  
  if (limitInfo && limitInfo.nextAllowedTime) {
    const now = Date.now();
    const remainingCooldown = limitInfo.nextAllowedTime - now;
    const reductionAmount = remainingCooldown * reductionFactor;
    
    limitInfo.nextAllowedTime = now + (remainingCooldown - reductionAmount);
    rateLimits[key] = limitInfo;
    saveRateLimits(rateLimits);
    
    console.log(`Reduced rate limit cooldown for ${key} by ${reductionFactor * 100}%`);
  }
}

export function shouldShowNotification(key: string): boolean {
  // Get timestamp of last notification for this key
  const lastShown = localStorage.getItem(`notification_${key}_lastshown`);
  const now = Date.now();
  
  // Only show each notification type once every 5 minutes maximum
  if (lastShown && now - parseInt(lastShown, 10) < 5 * 60 * 1000) {
    return false;
  }
  
  // Track this notification
  localStorage.setItem(`notification_${key}_lastshown`, now.toString());
  return true;
}

export function formatTimeRemaining(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  const mm = String(minutes).padStart(2, '0');
  const ss = String(remainingSeconds).padStart(2, '0');
  
  return `${mm}:${ss}`;
}

export function clearRateLimits(): void {
  try {
    localStorage.removeItem(RATE_LIMITS_KEY);
    console.log('Rate limits cleared from localStorage.');
  } catch (error) {
    console.error('Error clearing rate limits from localStorage:', error);
  }
}

export function getRemainingCooldownTime(key: string = 'global'): number {
  const rateLimits = getRateLimits();
  const limitInfo = rateLimits[key];

  if (!limitInfo || !limitInfo.nextAllowedTime) {
    return 0;
  }

  const now = Date.now();
  const remaining = Math.max(0, limitInfo.nextAllowedTime - now);
  
  return Math.ceil(remaining / 1000); // Return remaining seconds
}

export function setGlobalRateLimit(durationSeconds: number = 300): void {
  const now = Date.now();
  const nextAllowedTime = now + (durationSeconds * 1000);

  const rateLimits = getRateLimits();
  
  // Set rate limits for all known endpoints
  Object.keys(rateLimits).forEach(key => {
    rateLimits[key] = {
      ...rateLimits[key],
      nextAllowedTime,
      attempts: (rateLimits[key]?.attempts || 0) + 1,
      lastAttempt: now
    };
  });
  
  // Also set a global marker
  rateLimits['global'] = {
    nextAllowedTime,
    attempts: (rateLimits['global']?.attempts || 0) + 1,
    lastAttempt: now,
    lastSuccess: rateLimits['global']?.lastSuccess || 0
  };
  
  saveRateLimits(rateLimits);
  
  console.log(`Global rate limit set for ${durationSeconds} seconds until ${new Date(nextAllowedTime).toISOString()}`);
}

// New exported functions needed by other components
export function isInRateLimitCooldown(key: string = 'global'): boolean {
  const rateLimits = getRateLimits();
  const limitInfo = rateLimits[key];
  
  if (!limitInfo || !limitInfo.nextAllowedTime) {
    return false;
  }
  
  const now = Date.now();
  return limitInfo.nextAllowedTime > now;
}

export function isTooSoonForEndpoint(key: string): boolean {
  const rateLimits = getRateLimits();
  const limitInfo = rateLimits[key];
  
  if (!limitInfo || !limitInfo.lastAttempt) {
    return false;
  }
  
  const now = Date.now();
  const minInterval = 5000; // 5 seconds minimum between requests
  
  return (now - limitInfo.lastAttempt) < minInterval;
}

export function getTimeUntilNextAllowedRequest(key: string): number {
  const rateLimits = getRateLimits();
  const limitInfo = rateLimits[key];
  
  if (!limitInfo || !limitInfo.nextAllowedTime) {
    return 0;
  }
  
  const now = Date.now();
  return Math.max(0, Math.ceil((limitInfo.nextAllowedTime - now) / 1000));
}
